'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { apiFetch } from '@/lib/api'

export default function LoginPage() {
  const router = useRouter()
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register'
      const body = isLogin
        ? { email, password }
        : { email, password, name }

      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      })

      // Проверяем content-type перед парсингом JSON
      const contentType = response.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) {
        // Получили HTML вместо JSON — скорее всего middleware редирект
        setError('Ошибка авторизации. Попробуйте обновить страницу.')
        return
      }

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || 'Произошла ошибка')
        return
      }

      // Устанавливаем cookie вручную как fallback.
      // Set-Cookie от сервера может не применяться браузером
      // (Cloudflare, dev-режим Next.js, secure flag и т.д.)
      if (data.token) {
        const isSecure = window.location.protocol === 'https:'
        document.cookie = `auth_token=${data.token}; path=/; max-age=${7 * 24 * 60 * 60}; samesite=lax${isSecure ? '; secure' : ''}`
      }

      router.push('/')
    } catch (err) {
      console.error('Login error:', err)
      setError('Произошла ошибка при подключении к серверу')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #8B5A2B 0%, #5D4037 50%, #3E2723 100%)',
      }}
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(16px)',
          borderRadius: '14px',
          border: '1px solid rgba(139, 90, 43, 0.2)',
          boxShadow: '0 12px 40px rgba(139, 90, 43, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#3E2723' }}>
            Команда Подкова
          </h1>
          <p style={{ color: '#999', fontSize: '13px' }}>
            {isLogin ? 'Войдите в свой аккаунт' : 'Создайте новый аккаунт'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5D4037' }}>
                Имя
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full outline-none transition"
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1px solid rgba(139, 90, 43, 0.3)',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#3E2723',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#5D4037'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 90, 43, 0.3)'
                }}
                placeholder="Введите ваше имя"
                required={!isLogin}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#5D4037' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full outline-none transition"
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                border: '1px solid rgba(139, 90, 43, 0.3)',
                borderRadius: '8px',
                background: '#fff',
                color: '#3E2723',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#5D4037'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(139, 90, 43, 0.3)'
              }}
              placeholder="Введите email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#5D4037' }}>
              Пароль
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full outline-none transition"
                style={{
                  padding: '8px 36px 8px 12px',
                  fontSize: '13px',
                  border: '1px solid rgba(139, 90, 43, 0.3)',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#3E2723',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#5D4037'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 90, 43, 0.3)'
                }}
                placeholder="Введите пароль"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#5D4037'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#999'
                }}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-center py-2" style={{ color: '#e53e3e' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: '#2E8B57',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '14px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.background = '#247A4A'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2E8B57'
            }}
          >
            {loading ? 'Загрузка...' : (isLogin ? 'Войти' : 'Зарегистрироваться')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin)
              setError('')
            }}
            className="text-sm transition-colors"
            style={{ color: '#5D4037' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#3E2723'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#5D4037'
            }}
          >
            {isLogin
              ? 'Нет аккаунта? Зарегистрироваться'
              : 'Уже есть аккаунт? Войти'}
          </button>
        </div>

        <div
          className="mt-6 pt-6 text-center"
          style={{ borderTop: '1px solid rgba(139, 90, 43, 0.1)' }}
        >
          {process.env.NEXT_PUBLIC_MAIN_SITE_URL ? (
            <a
              href={process.env.NEXT_PUBLIC_MAIN_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm transition-colors"
              style={{ color: '#999' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#5D4037'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#999'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}>
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Вернуться на главный сайт
            </a>
          ) : (
            <button
              onClick={() => router.push('/')}
              className="text-sm transition-colors"
              style={{ color: '#999' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#5D4037'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#999'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}>
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Вернуться на главный сайт
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
